import React from 'react';
import { BASE_URL } from '@constants/UHEEndpoints';
import {
  ACCESS_TOKEN_KEY,
  HTTP_ERROR_MESSAGES,
  IMPERSONATE_TOKEN,
} from '@constants/UHESettings';
import {
  HTTPResponseError,
  HTTPResponseWarning,
  ServerError,
} from '@constants/UHEServerExceptions';
import { NotificationManager } from 'react-notifications';
import { createIntl, createIntlCache } from 'react-intl';
import AppLocale from 'lngProvider';
import { flattenMessages } from '@util/UheHelper';
import UheHelper from './UheHelper';

const cache = createIntlCache();

export const defaultPostHeaders = [
  {
    name: 'Content-Type',
    value: 'application/json',
  },
];

const RestManager = {
  formDataRequest: async (url, data, headers = [], isEssential = true, locale) => {
    const options = {
      method: 'POST',
      headers: RestManager.getHeaders(headers),
      body: data,
      locale,
    };
    const res = await fetch(BASE_URL + url, options).then(async (response) => {
      const hasError = RestManager.checkStatus(response, isEssential);
      const serverResponse = await RestManager.tryResponseJSON(response);
      if (hasError) {
        RestManager.handleErrorResponse(serverResponse, isEssential, locale);
      }
      return serverResponse;
    });

    return res;
  },

  requestNewFile: async (url, data, headers = [], isEssential = true, locale) => {
    const options = {
      method: 'POST',
      headers: RestManager.getHeaders(headers),
      body: data,
    };
    const json = await fetch(BASE_URL + url, options).then((response) => {
      RestManager.checkResponseStatus(response, isEssential, locale);
      return response.blob();
    });

    return json;
  },

  request: async (url, method = 'GET', headers = [], isEssential = true, locale) => {
    const options = {
      method,
      headers: RestManager.getHeaders([...defaultPostHeaders, ...headers]),
      mode: 'cors',
      cache: 'default',
    };
    const json = await fetch(BASE_URL + url, options).then((response) => {
      RestManager.checkResponseStatus(response, isEssential, locale);
      return RestManager.tryResponseJSON(response);
    });

    return json;
  },

  requestFile: async (url, method = 'GET', headers = [], isEssential = true, locale) => {
    const options = {
      method,
      headers: RestManager.getHeaders(headers),
      mode: 'cors',
      cache: 'default',
    };
    const json = await fetch(BASE_URL + url, options).then((response) => {
      RestManager.checkResponseStatus(response, isEssential, locale);
      return response.blob();
    });

    return json;
  },

  /**
   * @description Handles Request && Response for CSV Export
   * @param {Object} url
   * @param {string} method
   * @param {Object} headers
   * @param {boolean} isEssential
   * @returns {Object}
   */
  exportCsvFile: async (url, method = 'GET', headers = [], isEssential = true, intl) => {
    NotificationManager.success(
      '',
      intl.formatMessage({ id: 'export.notificationTitle' }),
    );
    const options = {
      method,
      headers: RestManager.getHeaders(headers),
      cache: 'no-cache',
    };
    const json = await fetch(url, options).then(async (response) => {
      RestManager.checkResponseStatus(response, isEssential);
      const data = await response.blob();
      const a = document.createElement('a');
      const csvUrl = window.URL.createObjectURL(data);
      const fileName = response.headers.get('content-disposition').split('filename*=')[1].split("UTF-8''").pop();

      if (data) {
        NotificationManager.listNotify.forEach((notification) => NotificationManager.remove({ id: notification.id }));
        NotificationManager.success(
          '',
          intl.formatMessage({ id: 'export.notificationTitleDone' }),
          5000,
        );
      }
      a.href = csvUrl;
      a.download = fileName;
      document.body.append(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(csvUrl);
    });

    return json;
  },

  tryResponseJSON: async (response) => {
    try {
      return await response.json();
    } catch (e) {
      console.log('tryResponseJSON: ', e);
      return {};
    }
  },

  requestWithoutQueryParams: async (
    url,
    method,
    bodyData = {},
    headers = [],
    isEssential = true,
    isAuthBearer = true,
    locale,
  ) => {
    const options = {
      method,
      headers: RestManager.getHeaders(
        [...defaultPostHeaders, ...headers],
        isAuthBearer,
      ),
      mode: 'cors',
      cache: 'default',
      body: JSON.stringify(bodyData),
    };
    const finalUrl = BASE_URL + url;
    const json = await fetch(finalUrl, options).then(async (response) => {
      const hasError = RestManager.checkStatus(response, isEssential);
      const serverResponse = await RestManager.tryResponseJSON(response);
      if (hasError) {
        RestManager.handleErrorResponse(serverResponse, isEssential, locale);
      }
      return serverResponse;
    });

    return json;
  },

  requestWithEmptyResponse: async (
    url,
    method,
    bodyData = {},
    headers = [],
    isEssential = true,
    isAuthBearer = true,
    locale,
  ) => {
    const options = {
      method,
      headers: RestManager.getHeaders(
        [...defaultPostHeaders, ...headers],
        isAuthBearer,
      ),
      mode: 'cors',
      cache: 'default',
      body: JSON.stringify(bodyData),
    };
    const finalUrl = BASE_URL + url;
    const json = await fetch(finalUrl, options).then(async (response) => {
      if (method === 'DELETE') {
        return { status: response.status };
      }
      const hasError = RestManager.checkStatus(response, isEssential);
      const serverResponse = await RestManager.tryResponseJSON(JSON.parse(response));
      if (hasError) {
        RestManager.handleErrorResponse(serverResponse, isEssential, locale);
      }
      return serverResponse;
    });

    return json;
  },


  /**
   * @description Creates Headers Object
   * @param {Object} additionalHeaders
   * @param {boolean} isAuthBearer
   * @returns {Object}
   */
  getHeaders(additionalHeaders, isAuthBearer = true) {
    const headers = new Headers();
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const impersonateToken = localStorage.getItem(IMPERSONATE_TOKEN);

    if (isAuthBearer) {
      impersonateToken !== null
        ? headers.append('Authorization', `Bearer ${impersonateToken}`)
        : headers.append('Authorization', `Bearer ${accessToken}`);
    }

    additionalHeaders.forEach((item) => {
      headers.append(item.name, item.value);
    });

    return headers;
  },
  /**
 *  Gets GWT token
 * @returns {String} token
 */
  getToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  /**
   * Checks Server Response Status Codes && Redirects
   * || Handling Errors Depending on the Status Code
   * @param {Object} response response
   * @param {boolean} isEssential isEssential
   * @param {boolean} locale locale
   * @returns {boolean|void} true|false
   */
  checkResponseStatus(response, isEssential = true, locale) {
    if (
      response.status === 401
      && window.location.pathname.indexOf('/activate-user') === -1
    ) {
      UheHelper.showModal('warning', 'REACT_APP_EXPIRED_SESSION_MSG', 'Ok');
    } else if (response.status < 200 || response.status > 204) {
      RestManager.handleErrorResponse(response, isEssential, locale);
    }
  },

  /**
   * Checks Server Response Status Codes && Redirects Depending on the Status Code
   * @param {Object} response response
   * @param {boolean} isEssential isEssential
   * @returns {boolean|void} true|false
   */
  checkStatus(response, isEssential = true) {
    if (
      response.status === 401
      && window.location.pathname.indexOf('/activate-user') === -1
    ) {
      UheHelper.showModal('warning', 'REACT_APP_EXPIRED_SESSION_MSG', 'Ok');
    } else if (response.status < 200 || response.status > 204) {
      return true;
    }
    return false;
  },

  /**
   * Handle Errors that are Coming as Response from the Server
   * @param {Object} response
   * @param {boolean} isEssential
   * @param {String} locale
   * @returns {void}
   */
  handleErrorResponse(response, isEssential, locale = 'en') {
    let error;
    const currentAppLocale = AppLocale[locale];
    const { formatMessage } = createIntl({ locale: currentAppLocale.locale, messages: flattenMessages(currentAppLocale.messages) }, cache);

    switch (response.status) {
      case 400:
        if (isEssential && response.message === 'Missing token') {
          error = new HTTPResponseError('missing_token', response.status);
        } else if (isEssential && response.message === 'Invalid token') {
          error = new HTTPResponseError('invalid_token', response.status);
        } else if (isEssential && response.message === 'The \'email\' is already in use') {
          error = new HTTPResponseError('email_use', response.status);
        } else if (isEssential && response.message === 'Password too short') {
          error = new HTTPResponseError('short_password', response.status);
        } else if (isEssential && response.message === 'Password too simple') {
          error = new HTTPResponseError('simple_password', response.status);
        } else if (isEssential) {
          error = new HTTPResponseError(
            response.message,
            response.status,
          );
        } else {
          error = new HTTPResponseWarning(
            response.message,
            response.status,
          );
        }
        break;
      case 403:
      case 404:
      case 409:
        if (isEssential) {
          error = new HTTPResponseError(
            HTTP_ERROR_MESSAGES[response.status],
            response.status,
          );
        } else {
          error = new HTTPResponseWarning(
            HTTP_ERROR_MESSAGES[response.status],
            response.status,
          );
        }
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        error = new ServerError(HTTP_ERROR_MESSAGES[response.status]);
        break;
      default:
        error = new HTTPResponseError(response.message || (response.failure ? formatMessage({ id: 'common.noOnlineDevice' }) : ''), response.status);
    }

    throw error;
  },
};

export default RestManager;
